import Phaser from "phaser";
import { Block } from "./Block";
import { StageConfig } from "../data/StageConfig";

/** Block colors for the tower (bottom to top) */
const BLOCK_COLORS = [
  0xe74c3c, // red
  0x3498db, // blue
  0x2ecc71, // green
  0xf39c12, // orange
  0x9b59b6, // purple
  0x1abc9c, // teal
  0xe67e22, // dark orange
  0x2980b9, // dark blue
];

export class Tower {
  public blocks: Block[] = [];
  public darumaHead: Block | null = null;

  private scene: Phaser.Scene;
  private baseX: number;
  private baseY: number;
  private ground: MatterJS.BodyType | null = null;

  constructor(scene: Phaser.Scene, baseX: number, baseY: number) {
    this.scene = scene;
    this.baseX = baseX;
    this.baseY = baseY;
  }

  /** Build the tower with blocks and daruma head on top */
  build(blockCount: number): void {
    this.clear();

    const { blockWidth, blockHeight, darumaHeadWidth, darumaHeadHeight } = StageConfig;

    // Create ground (static invisible platform)
    this.ground = this.scene.matter.add.rectangle(
      this.baseX,
      this.baseY + blockHeight / 2 + 2,
      blockWidth + 40,
      10,
      { isStatic: true, label: "ground", friction: 1.0 }
    ) as MatterJS.BodyType;

    // Create blocks from bottom to top
    for (let i = 0; i < blockCount; i++) {
      const y = this.baseY - i * blockHeight;
      const color = BLOCK_COLORS[i % BLOCK_COLORS.length];

      const block = new Block(this.scene, {
        x: this.baseX,
        y,
        width: blockWidth,
        height: blockHeight,
        color,
        index: i,
      });

      this.blocks.push(block);
    }

    // Create daruma head on top
    const headY = this.baseY - blockCount * blockHeight - darumaHeadHeight / 2 + blockHeight / 2;
    this.darumaHead = new Block(this.scene, {
      x: this.baseX,
      y: headY,
      width: darumaHeadWidth,
      height: darumaHeadHeight,
      color: 0xf5f5dc,
      isDarumaHead: true,
      index: blockCount,
    });
  }

  /** Calculate the tilt of the tower based on top-bottom center X offset */
  calculateTilt(): number {
    const activeBlocks = this.getActiveBlocks();
    if (activeBlocks.length < 2 && this.darumaHead && !this.darumaHead.isRemoved) {
      // If only one block left + head, measure head offset from base
      const head = this.darumaHead;
      return Math.abs(head.getX() - this.baseX) / 100;
    }

    if (activeBlocks.length < 2) return 0;

    const bottom = activeBlocks[0];
    const top = activeBlocks[activeBlocks.length - 1];
    const dx = top.getX() - bottom.getX();
    const dy = bottom.getY() - top.getY();

    if (dy <= 0) return Math.PI / 2; // tower collapsed

    return Math.abs(Math.atan2(dx, dy));
  }

  /** Get blocks that are still active (not escaping, not removed) */
  getActiveBlocks(): Block[] {
    return this.blocks.filter((b) => !b.isEscaping && !b.isRemoved);
  }

  /** Get the block at a given Y coordinate (returns the closest block) */
  getBlockAtY(y: number): Block | null {
    let closest: Block | null = null;
    let minDist = Infinity;

    for (const block of this.blocks) {
      if (block.isEscaping || block.isRemoved) continue;

      const dist = Math.abs(block.getY() - y);
      if (dist < minDist && dist < StageConfig.blockHeight) {
        minDist = dist;
        closest = block;
      }
    }

    return closest;
  }

  /** Remove a block from the tower */
  removeBlock(block: Block): void {
    block.destroy();
  }

  /** Clear all blocks */
  clear(): void {
    for (const block of this.blocks) {
      if (!block.isRemoved) block.destroy();
    }
    if (this.darumaHead && !this.darumaHead.isRemoved) {
      this.darumaHead.destroy();
    }
    if (this.ground) {
      this.scene.matter.world.remove(this.ground);
      this.ground = null;
    }
    this.blocks = [];
    this.darumaHead = null;
  }

  /** Update all block visuals */
  update(): void {
    for (const block of this.blocks) {
      block.update();
    }
    if (this.darumaHead && !this.darumaHead.isRemoved) {
      this.darumaHead.update();
    }
  }
}
